import { useEffect, useRef, useState } from 'react'
import { Package, Search, X } from 'lucide-react'
import { useProcessList } from '../../../occurrences/hooks/useProcessList'
import {
  NO_ANALYSTS,
  type ProcessListItem,
} from '../../../occurrences/types/occurrences'

interface ProcessLinkPickerProps {
  selected: ProcessListItem | null
  onSelect: (process: ProcessListItem) => void
  onClear: () => void
  /** Restringe a busca a estas empresas (vazio/ausente = todas do escopo). */
  companyIds?: number[]
}

/**
 * Busca + seleção manual de um processo do escopo do usuário — usado no
 * formulário "Novo problema" quando NÃO há processo detectado por contexto
 * (`relatedProcessId`) e no "Nova ocorrência" da página inicial de
 * Ocorrências (filtrado pela empresa escolhida, `companyIds`). Reaproveita `useProcessList`/
 * `GET /occurrences/processes` (mesmo motor de busca da lista de
 * Ocorrências — debounce, escopo por empresa já resolvido no backend), só
 * que num combobox compacto em vez de lista fixa.
 *
 * Só é montado quando `capabilities.canAccessOccurrences` (ver
 * `NewIssueForm`) — o endpoint que ele consome está atrás do gate do módulo
 * de Ocorrências, então não faz sentido oferecer a busca pra quem não tem
 * acesso a ela (o resto do formulário "Novo problema" continua funcionando
 * normalmente sem isso).
 */
export function ProcessLinkPicker({
  selected,
  onSelect,
  onClear,
  companyIds,
}: ProcessLinkPickerProps) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const { data, isLoading } = useProcessList({
    search: debouncedSearch || undefined,
    scope: companyIds ? { companyIds, analysts: NO_ANALYSTS } : undefined,
    pageSize: 20,
  })
  const results = data?.items ?? []

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-[5px] border border-border-subtle bg-surface px-3 py-2 text-xs">
        <span className="flex min-w-0 items-center gap-1.5">
          <Package className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden />
          <span className="truncate">
            <strong>{selected.process_number ?? `#${selected.id}`}</strong>
            {selected.company && <> — {selected.company}</>}
          </span>
        </span>
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 rounded p-0.5 text-subtle hover:bg-surface-muted hover:text-body"
          aria-label="Remover processo vinculado"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-subtle"
          aria-hidden
        />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            // Esc fecha só a lista de resultados; `preventDefault` avisa quem
            // está em volta (ex.: o diálogo de nova ocorrência) que já tratou.
            if (e.key === 'Escape' && open) {
              e.preventDefault()
              setOpen(false)
            }
          }}
          placeholder="Buscar processo pelo número ou empresa…"
          className="w-full rounded-[5px] border border-border-subtle bg-surface py-1.5 pl-8 pr-3 text-xs text-body placeholder:text-subtle focus:border-[#2563eb] focus:outline-none"
        />
      </div>
      {open && (search.trim().length > 0 || results.length > 0) && (
        <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-[5px] border border-border-subtle bg-surface shadow-lg">
          {isLoading && (
            <p className="px-3 py-2 text-xs text-subtle">Buscando…</p>
          )}
          {!isLoading && results.length === 0 && (
            <p className="px-3 py-2 text-xs text-subtle">
              Nenhum processo encontrado.
            </p>
          )}
          {!isLoading &&
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onSelect(p)
                  setOpen(false)
                  setSearch('')
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition hover:bg-surface-muted"
              >
                <Package className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden />
                <span className="min-w-0 truncate">
                  <strong>{p.process_number ?? `#${p.id}`}</strong>
                  {p.company && <> — {p.company}</>}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  )
}
